import { PortalMaterialResponse } from "../contracts/PortalMaterialResponse";
import { MaterialAudience } from "../enums/MaterialAudience";

/** One heading of the Library: the files shared with the member, one group, or everyone. */
export interface PortalMaterialGroup {
  key: string;
  audience: MaterialAudience;
  /** The group's name when `audience` is Group. */
  groupName: string | null;
  materials: PortalMaterialResponse[];
}

/** What the viewer can show in place; null for a download-only file. */
export type PortalMaterialKind = "pdf" | "image" | "audio" | "video";

/**
 * Groups the Library in the order the member reads it: shared with them, then each of their
 * groups (by name), then everyone. Empty groups are left out; rows keep the server's order.
 */
export const groupPortalMaterials = (materials: PortalMaterialResponse[]): PortalMaterialGroup[] => {
  const own = materials.filter((material) => material.sharedWith === MaterialAudience.Member);
  const everyone = materials.filter((material) => material.sharedWith === MaterialAudience.Everyone);
  const byGroup = new Map<string, PortalMaterialResponse[]>();
  materials
    .filter((material) => material.sharedWith === MaterialAudience.Group)
    .forEach((material) => {
      const name = material.sharedWithGroupName ?? "";
      byGroup.set(name, [...(byGroup.get(name) ?? []), material]);
    });

  const groups: PortalMaterialGroup[] = [
    { key: "member", audience: MaterialAudience.Member, groupName: null, materials: own },
    ...[...byGroup.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, items]) => ({ key: `group:${name}`, audience: MaterialAudience.Group, groupName: name, materials: items })),
    { key: "everyone", audience: MaterialAudience.Everyone, groupName: null, materials: everyone },
  ];
  return groups.filter((group) => group.materials.length > 0);
};

/** The viewer's kind for a content type (parameters ignored), or null when it only downloads. */
export const portalMaterialKind = (contentType: string): PortalMaterialKind | null => {
  const mediaType = contentType.split(";")[0].trim().toLowerCase();
  if (mediaType === "application/pdf") return "pdf";
  if (mediaType.startsWith("image/")) return "image";
  if (mediaType.startsWith("audio/")) return "audio";
  if (mediaType.startsWith("video/")) return "video";
  return null;
};

/** The file's extension in capitals ("DOCX"), the short label of a download-only file. */
export const fileExtensionLabel = (fileName: string): string => {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 && dot < fileName.length - 1 ? fileName.slice(dot + 1).toUpperCase() : "";
};
